import React from 'react';

import { ATTRIBUTE_LIMIT } from '@neptune/shared/attributes-domain';
import { useMemoizedValue } from '@neptune/shared/common-util';
import { ShortIdsByProject } from '@neptune/shared/entity-domain';
import { ScopedQueryFilterSearch, SEARCH_MODES, SearchMode } from '@neptune/shared/search-domain';

import { isNotUndefined } from 'common/tsHelpers';
import { AttributeDefinition } from 'domain/experiment/attribute';

import { useAttributeSearchSpaceTrace } from './use-attribute-search-space-trace';
import {
  createQueryAttributeDefinitionDataSource,
  QueryAttributeDefinitionDataSourceBase,
  QueryAttributeDefinitionDataSourceMultiProject,
  QueryAttributeDefinitionDataSourceMultiProjectsNoShortIds,
  QueryAttributeDefinitionDataSourceParams,
  QueryAttributeDefinitionDataSourceSingleProject,
} from './use-query-attribute-definition-data-source';

function hasExplicitScope<T>(params: QueryAttributeDefinitionDataSourceParams<T>): boolean {
  return (
    ('shortIds' in params && params.shortIds !== undefined) ||
    ('shortIdsByProject' in params && params.shortIdsByProject !== undefined)
  );
}

function hasAnyShortIds(shortIdsByProject: ShortIdsByProject): boolean {
  for (const ids of shortIdsByProject.values()) {
    if (ids.length > 0) {
      return true;
    }
  }

  return false;
}

function resolveProjectIdentifiers<T>(params: QueryAttributeDefinitionDataSourceParams<T>) {
  return [...(params.projectIdentifiers ?? []), params.projectIdentifier].filter(isNotUndefined);
}

function pickBaseParams<T>(
  params: QueryAttributeDefinitionDataSourceParams<T>,
): QueryAttributeDefinitionDataSourceBase<T> {
  return {
    enhancer: params.enhancer,
    filter: params.filter,
    filterAttributeTypes: params.filterAttributeTypes,
    limit: params.limit,
    prefillAttributes: params.prefillAttributes,
    subpropertyStrategy: params.subpropertyStrategy,
  };
}

function buildScopedParams<T>(
  params: QueryAttributeDefinitionDataSourceParams<T>,
  shortIdsByProject: ShortIdsByProject,
): QueryAttributeDefinitionDataSourceMultiProject<T> {
  return {
    ...pickBaseParams(params),
    shortIdsByProject,
  };
}

export function useScopedQueryAttributeDefinitionDataSource(
  params: QueryAttributeDefinitionDataSourceMultiProject<AttributeDefinition>,
): ScopedQueryFilterSearch<AttributeDefinition>;
export function useScopedQueryAttributeDefinitionDataSource(
  params: QueryAttributeDefinitionDataSourceSingleProject<AttributeDefinition>,
): ScopedQueryFilterSearch<AttributeDefinition>;
export function useScopedQueryAttributeDefinitionDataSource(
  params: QueryAttributeDefinitionDataSourceMultiProjectsNoShortIds<AttributeDefinition>,
): ScopedQueryFilterSearch<AttributeDefinition>;

export function useScopedQueryAttributeDefinitionDataSource(
  params: QueryAttributeDefinitionDataSourceParams<AttributeDefinition>,
): ScopedQueryFilterSearch<AttributeDefinition> {
  const { getShortIdsByProject } = useAttributeSearchSpaceTrace();

  const limit = params.limit ?? ATTRIBUTE_LIMIT;

  const scopedParams = useMemoizedValue({ ...params, limit });

  return React.useCallback(
    async (query: string, searchMode?: SearchMode) => {
      if (searchMode === SEARCH_MODES.FULL || hasExplicitScope(scopedParams)) {
        const result = await createQueryAttributeDefinitionDataSource(scopedParams)(query);
        return {
          ...result,
          searchMode: SEARCH_MODES.FULL,
        };
      }

      const trace = getShortIdsByProject(resolveProjectIdentifiers(scopedParams));

      if (!hasAnyShortIds(trace)) {
        const result = await createQueryAttributeDefinitionDataSource(scopedParams)(query);
        return {
          ...result,
          searchMode: SEARCH_MODES.FULL,
        };
      }

      const result = await createQueryAttributeDefinitionDataSource(
        buildScopedParams(scopedParams, trace),
      )(query);

      return {
        ...result,
        searchMode: SEARCH_MODES.SCOPED,
        needsFullSearch: result.entries.length < scopedParams.limit,
      };
    },
    [scopedParams, getShortIdsByProject],
  );
}
